import React from 'react';

import './css/Benchmark.css'

import { useBarReveal } from './barReveal'

import { useCursorRimLight } from './rimLight'

import {
	BENCHMARK_CAVEATS,
	BENCHMARK_GROUPS,
	BENCHMARK_MAX,
	BENCHMARK_RUN,
	FormatGroup,
	Measure,
	P95_WINS,
	RATE_WINS,
	SCENARIO_COUNT,
	SPEEDUP_MAX,
	SPEEDUP_MEDIAN,
	SPEEDUP_OVERALL,
	Scenario,
	WATERMARK_COST_MAX,
	WATERMARK_RUNS,
	WatermarkRun,
	speedup,
} from './metrics'


function formatRate( rate: number ): string {
	return rate.toFixed( 1 )
}

// Thousands separated, as "3,414 ms" reads at a glance where "3414 ms"
// does not.
function formatLatency( ms: number ): string {
	return `${ ms.toLocaleString( 'en-US' ) } ms`
}

// A bar's length as a share of the longest bar of the page, handed to the
// stylesheet, which scales every bar by the same factor so the value printed
// at the end of the longest one still has somewhere to sit, and by a
// smaller one on a phone. The proportions between bars are untouched.
function barShare( rate: number ): number {
	return rate / BENCHMARK_MAX
}

function barStyle( share: number ): React.CSSProperties {
	return { '--share': share } as React.CSSProperties
}

// The time a full-length bar takes, one per group of rows so the formats do
// not all move at the same pace (barReveal.tsx). Cycled over the groups.
const GROUP_SPEEDS_S: number[] = [ 1.1, 1.45, 0.95, 1.3, 1.2 ]

function groupStyle( index: number ): React.CSSProperties {
	return { '--speed': `${ GROUP_SPEEDS_S[ index % GROUP_SPEEDS_S.length ] }s` } as React.CSSProperties
}

///@brief   The rows a chart draws one by one: every scenario, not the header.
const ROW_SELECTOR: string = '[data-bar-row]'

///@brief   On a row: the longer of its two bars, which is when its verdict
///         can appear.
function rowStyle( shares: number[] ): React.CSSProperties {
	return { '--longest': Math.max( ...shares ) } as React.CSSProperties
}

function formatSpeedup( scenario: Scenario ): string {
	return `${ speedup( scenario ).toFixed( 1 ) }×`
}

// A scenario is only a win worth colouring if it is meaningfully above
// parity; AVIF passthrough lands at 1.06× and should read as a tie, not as a
// win. BMP resize, at 0.94×, is a loss and is coloured as one.
function speedupClass( scenario: Scenario ): string {
	const ratio: number = speedup( scenario )
	if ( ratio >= 1.1 ) return ' Benchmark-Speedup-Win'
	if ( ratio <= 0.95 ) return ' Benchmark-Speedup-Loss'
	return ''
}

// A pair of bars, ours over the reference, each with its value at its end
// and its p95 in the column beside it. --share goes on each bar's row and
// on its p95, so the stylesheet can time the bar and what appears when it
// arrives from the one number.
function BarPair( { us, them, themClass }: { us: Measure, them: Measure, themClass?: string } ): React.JSX.Element {

	const usStyle: React.CSSProperties = barStyle( barShare( us.rate ) )
	const themStyle: React.CSSProperties = barStyle( barShare( them.rate ) )

	return (
		<>
			<div className="Benchmark-Bars" >
				<div className="Benchmark-Bar-Row" style={ usStyle } >
					<div className="Benchmark-Fill Benchmark-Fill-Us" />
					<div className="Benchmark-Value fs-16" >{ formatRate( us.rate ) }</div>
				</div>
				<div className="Benchmark-Bar-Row" style={ themStyle } >
					<div className={ `Benchmark-Fill${ themClass ?? '' }` } />
					<div className="Benchmark-Value Benchmark-Value-Them fs-16" >{ formatRate( them.rate ) }</div>
				</div>
			</div>

			<div className="Benchmark-Latencies" >
				<div className="Benchmark-Latency fs-16" style={ usStyle } >{ formatLatency( us.p95 ) }</div>
				<div className="Benchmark-Latency Benchmark-Latency-Them fs-16" style={ themStyle } >{ formatLatency( them.p95 ) }</div>
			</div>
		</>
	)
}

function ScenarioRow( { scenario }: { scenario: Scenario } ): React.JSX.Element {

	return (
		<div className="Benchmark-Scenario" style={ rowStyle( [ barShare( scenario.underlayer.rate ), barShare( scenario.imgproxy.rate ) ] ) } data-bar-row >
			<div className="Benchmark-Scenario-Name fs-16" >{ scenario.treatment }</div>
			<BarPair us={ scenario.underlayer } them={ scenario.imgproxy } />
			<div className={ `Benchmark-Speedup fs-16${ speedupClass( scenario ) }` } >{ formatSpeedup( scenario ) }</div>
		</div>
	)
}

// The column headings, on the same grid as the rows so each sits over its
// column.
function ChartHeader( { factor }: { factor: string } ): React.JSX.Element {

	return (
		<div className="Benchmark-Scenario Benchmark-Header fs-16" >
			<div />
			<div>images served per second, higher is better</div>
			<div className="Benchmark-Header-Latency" >p95 latency</div>
			<div className="Benchmark-Header-Factor" >{ factor }</div>
		</div>
	)
}

function MethodNotes(): React.JSX.Element {

	return (
		<div className="Benchmark-Method fs-16" data-fade data-fade-delay="0.2" >
			{ BENCHMARK_RUN.map( ( line: string ): React.JSX.Element => (
				<div className="Benchmark-Method-Line" key={ line } >{ line }</div>
			) ) }
		</div>
	)
}


export function Benchmark(): React.JSX.Element {

	// The stat frames catch the same rim light as the home page's cards.
	const statsRef = React.useRef<HTMLDivElement>( null )
	useCursorRimLight( statsRef, '.Frame' )

	// The chart draws itself row by row as they reach the screen.
	const chartRef = useBarReveal( ROW_SELECTOR )

	return (
		<div className="Pane-Light Page-Section Page-Section-First Benchmark" >
			<div className="Delimiter" />
			<div className="Container" >
				<div className="Page-Label" >Performances</div>
				<div className="Title text-shadow-2" data-fade >Benchmarked against the fastest.</div>

				<div className="Content Benchmark-Content" >

					<div className="Benchmark-Copy" data-fade >
						<div className="Benchmark-Text fs-18" >
							Same machine, same images, same load. Both servers run side by side on one box,
							64 concurrent clients hit each of them for 15 seconds per scenario, and the
							images go out in the format they came in. Five formats, three treatments each:
							passed through at full size, resized, or blurred.
						</div>
						<div className="Benchmark-Text fs-18" >
							On { RATE_WINS } of the { SCENARIO_COUNT } scenarios underlayer serves more images
							per second than imgproxy, by a median of { SPEEDUP_MEDIAN } and up to { SPEEDUP_MAX } on
							PNG, and on { P95_WINS } of them it answers sooner at the 95th percentile. The one
							scenario imgproxy wins is BMP resize. Every scenario weighted the same, underlayer
							comes out { SPEEDUP_OVERALL } ahead.
						</div>
					</div>

					<div className="Benchmark-Stats" ref={ statsRef } data-fade data-fade-delay="0.1" >
						<div className="Frame Benchmark-Stat" >
							<div className="Benchmark-Stat-Value" >{ SPEEDUP_MEDIAN }</div>
							<div className="Benchmark-Stat-Label fs-16" >median throughput advantage, { SPEEDUP_OVERALL } with every scenario weighted the same</div>
						</div>
						<div className="Frame Benchmark-Stat" >
							<div className="Benchmark-Stat-Value" >{ SPEEDUP_MAX }</div>
							<div className="Benchmark-Stat-Label fs-16" >largest gap, on PNG passthrough</div>
						</div>
					</div>

					<div className="Benchmark-Chart" ref={ chartRef } data-fade data-fade-delay="0.15" >

						<div className="Benchmark-Legend fs-16" >
							<span className="Benchmark-Legend-Item" ><span className="Benchmark-Swatch Benchmark-Swatch-Us" />underlayer</span>
							<span className="Benchmark-Legend-Item" ><span className="Benchmark-Swatch" />imgproxy</span>
							<span className="Benchmark-Legend-Unit" >all bars of this page share one scale</span>
						</div>

						<ChartHeader factor="factor" />

						{ BENCHMARK_GROUPS.map( ( group: FormatGroup, g: number ): React.JSX.Element => (
							<div className="Benchmark-Group" style={ groupStyle( g ) } key={ group.format } >
								<div className="Benchmark-Group-Title fs-18" >{ group.format }</div>
								{ group.scenarios.map( ( scenario: Scenario ): React.JSX.Element => (
									<ScenarioRow scenario={ scenario } key={ scenario.treatment } />
								) ) }
							</div>
						) ) }

					</div>

					<MethodNotes />

					<div className="Benchmark-Caveats" data-fade data-fade-delay="0.25" >
						<div className="Benchmark-Caveats-Title fs-16" >Reading the numbers</div>
						{ BENCHMARK_CAVEATS.map( ( line: string ): React.JSX.Element => (
							<div className="Benchmark-Caveat fs-16" key={ line } >{ line }</div>
						) ) }
					</div>

				</div>
			</div>
		</div>
	)
}


// The mark is signed as a cost, so "−18%" reads as what it takes and "+2%"
// as a run that came out a touch faster, which is noise.
function formatCost( run: WatermarkRun ): string {
	if ( run.cost > 0 ) return `−${ run.cost }%`
	if ( run.cost < 0 ) return `+${ -run.cost }%`
	return '0%'
}

function WatermarkRow( { run }: { run: WatermarkRun } ): React.JSX.Element {

	return (
		<div className="Benchmark-Scenario" style={ rowStyle( [ barShare( run.marked.rate ), barShare( run.bare.rate ) ] ) } data-bar-row >
			<div className="Benchmark-Scenario-Name Benchmark-Scenario-Format fs-16" >{ run.format }</div>
			<BarPair us={ run.marked } them={ run.bare } themClass=" Benchmark-Fill-Bare" />
			<div className="Benchmark-Speedup Benchmark-Cost fs-16" >{ formatCost( run ) }</div>
		</div>
	)
}

export function WatermarkBenchmark(): React.JSX.Element {

	// Its own watch: this chart is further down the page and draws itself
	// when the reader gets to it, not while the first one is on screen.
	const chartRef = useBarReveal( ROW_SELECTOR )

	return (
		<div className="Pane-Light Page-Section Benchmark Benchmark-Watermark" >
			<div className="Delimiter" />
			<div className="Container" >
				<div className="Page-Label" >Watermarking</div>
				<div className="Title text-shadow-2" data-fade >The invisible mark, at the same pace.</div>

				<div className="Content Benchmark-Content" >

					<div className="Benchmark-Copy" data-fade >
						<div className="Benchmark-Text fs-18" >
							Same run, same machine, same load: each format is served at full size with an
							invisible identifier written into every image. underlayer alone is measured here,
							since imgproxy has no invisible watermarking and there is nothing to set it
							against, so each run is compared with underlayer's own passthrough run of the
							same format, above.
						</div>
						<div className="Benchmark-Text fs-18" >
							The mark is computed on the GPU, and what it costs depends on how much of the
							time the format's own encoder takes. On JPEG, the fastest pipeline, it takes
							{ ' ' }{ WATERMARK_COST_MAX } of the throughput; on PNG and WebP a fifth or less; on
							AVIF, where the encoder dominates, it is not measurable.
						</div>
					</div>

					<div className="Benchmark-Chart" ref={ chartRef } data-fade data-fade-delay="0.15" >

						<div className="Benchmark-Legend fs-16" >
							<span className="Benchmark-Legend-Item" ><span className="Benchmark-Swatch Benchmark-Swatch-Us" />marked</span>
							<span className="Benchmark-Legend-Item" ><span className="Benchmark-Swatch Benchmark-Swatch-Bare" />passthrough, no mark</span>
							<span className="Benchmark-Legend-Unit" >same scale as the chart above</span>
						</div>

						<ChartHeader factor="cost" />

						<div className="Benchmark-Group" style={ groupStyle( 1 ) } >
							{ WATERMARK_RUNS.map( ( run: WatermarkRun ): React.JSX.Element => (
								<WatermarkRow run={ run } key={ run.format } />
							) ) }
						</div>

					</div>

					<MethodNotes />

				</div>
			</div>
		</div>
	)
}
